import { useState, useMemo, useEffect, useCallback, ChangeEvent } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAEDashboard } from '../hooks/useAEDashboard';
import { useFunnel, FunnelStage } from '../hooks/useFunnel';
import { useActivityMetrics } from '../hooks/useActivityMetrics';
import { useSalesforceUsers } from '../hooks/useSalesforceUsers';
import { TimePeriod, TIME_PERIOD_OPTIONS } from '../utils/timePeriods';
import { OpportunityRow } from '../types/gtm';
import { formatNumber, formatDate, formatCurrency } from '../utils/formatters';
import SearchableSelect from '../components/SearchableSelect';
import KPICard from '../components/KPICard';
import DataTable from '../components/DataTable';
import { getPicklistValues, updateRecord } from '../services/salesforce';
import { SF_BASE } from '../constants';

// ── Editable stage dropdown (subtle: green = won, red = lost, blue = open) ──

function stageColorClasses(stage: string): string {
  if (stage === 'Closed Won') return 'bg-green-50 text-green-800 border-green-300';
  if (stage === 'Closed Lost') return 'bg-red-50 text-red-800 border-red-300';
  return 'bg-blue-50 text-blue-800 border-blue-300';
}

function StageSelect({
  oppId,
  value,
  options,
  onSaved,
}: {
  oppId: string;
  value: string;
  options: string[];
  onSaved: () => void;
}) {
  const [current, setCurrent] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  // Reconcile with refreshed data when the underlying value changes.
  useEffect(() => {
    setCurrent(value);
  }, [value]);

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value;
      const prev = current;
      if (next === prev) return;
      setCurrent(next);
      setSaving(true);
      setError(false);
      try {
        await updateRecord('Opportunity', oppId, { StageName: next });
        onSaved();
      } catch {
        setCurrent(prev);
        setError(true);
      } finally {
        setSaving(false);
      }
    },
    [oppId, current, onSaved]
  );

  // Keep a value already set in Salesforce selectable even if it is no longer
  // an active picklist option.
  const allOptions = current && !options.includes(current) ? [current, ...options] : options;

  return (
    <select
      value={current}
      onChange={handleChange}
      disabled={saving || options.length === 0}
      title={error ? 'Failed to save — try again' : 'Change stage'}
      className={`text-xs font-medium border rounded-full px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-eep-navy ${
        error ? 'bg-red-50 text-red-700 border-red-400' : stageColorClasses(current)
      } ${saving ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
    >
      {allOptions.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

// ── Opportunity columns ──

const aeColumnHelper = createColumnHelper<OpportunityRow>();

function buildAeColumns(
  stageOptions: string[],
  onStageSaved: () => void
): ColumnDef<OpportunityRow, unknown>[] {
  return [
  aeColumnHelper.accessor('Name', {
    header: 'Name',
    cell: (info) => (
      <a
        href={`${SF_BASE}/${info.row.original.Id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        {info.getValue()}
      </a>
    ),
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.accessor('StageName', {
    header: 'Stage',
    cell: (info) => (
      <StageSelect
        oppId={info.row.original.Id}
        value={info.getValue()}
        options={stageOptions}
        onSaved={onStageSaved}
      />
    ),
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.accessor('Amount', {
    header: 'Amount',
    cell: (info) => formatCurrency(info.getValue()),
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.accessor('LeadSource', {
    header: 'Lead Source',
    cell: (info) => info.getValue() ?? '—',
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.display({
    id: 'Age',
    header: 'Age (days)',
    cell: (info) => {
      const { StageName, CreatedDate, CloseDate } = info.row.original;
      const start = new Date(CreatedDate);
      const end = StageName === 'Closed Won' || StageName === 'Closed Lost'
        ? new Date(CloseDate)
        : new Date();
      return Math.floor((end.getTime() - start.getTime()) / 86_400_000);
    },
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.accessor('CloseDate', {
    header: 'Close Date',
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.accessor('LastActivityDate', {
    header: 'Last Activity',
    cell: (info) => (info.getValue() ? formatDate(info.getValue()) : '—'),
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.display({
    id: 'AccountName',
    header: 'Account',
    cell: (info) => info.row.original.Account?.Name ?? '—',
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.display({
    id: 'Households',
    header: '# Households',
    cell: (info) => info.row.original.Number_of_Households__c ?? '—',
  }) as ColumnDef<OpportunityRow, unknown>,
  aeColumnHelper.accessor('NextStep', {
    header: 'Next Steps',
    cell: (info) => info.getValue() ?? '—',
  }) as ColumnDef<OpportunityRow, unknown>,
  ];
}

// ── Conversion funnel ──

function FunnelSection({ stages }: { stages: FunnelStage[] }) {
  const chartData = stages.map((s) => ({ name: s.label, count: s.count }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-gray-900">Conversion Funnel</h2>
        <div className="flex items-center gap-2 text-xs">
          {stages.slice(1).map((s) => (
            <span key={s.label} className="px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              {s.conversionFromPrev ?? '—'} → {s.label}
            </span>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 40, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={130} />
          <Tooltip />
          <Bar
            dataKey="count"
            fill="#2D3250"
            radius={[0, 4, 4, 0]}
            label={{ position: 'right', fontSize: 12, fill: '#374151' }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Breakdown card (value + grouped horizontal bar) ──

function buildCounts(
  rows: OpportunityRow[],
  getKey: (o: OpportunityRow) => string | null
): { name: string; count: number }[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    const key = getKey(row) || 'Unknown';
    map.set(key, (map.get(key) || 0) + 1);
  }
  return Array.from(map.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

interface BreakdownCardProps {
  title: string;
  rows: OpportunityRow[];
  getKey: (o: OpportunityRow) => string | null;
}

function BreakdownCard({ title, rows, getKey }: BreakdownCardProps) {
  const chartData = useMemo(() => buildCounts(rows, getKey), [rows, getKey]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-5 pb-3">
        <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{formatNumber(rows.length)}</p>
      </div>
      <div className="border-t border-gray-100 px-5 pb-5 pt-3">
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No data</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 32)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
              <Tooltip />
              <Bar dataKey="count" fill="#2D3250" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ── Sales dashboard ──

export default function GTMDashboard() {
  const [aeId, setAeId] = useState('');
  const [period, setPeriod] = useState<TimePeriod>('this_quarter');
  const [source, setSource] = useState('');
  const [stageOptions, setStageOptions] = useState<string[]>([]);

  const { userOptions, loading: usersLoading } = useSalesforceUsers();
  const { demosScheduledCount, opportunities, loading, error, refetch } =
    useAEDashboard(aeId || null, period, source || null);
  const { stages, refetch: refetchFunnel } = useFunnel(aeId || null, period);

  // Completed activity (Calls / Emails / Tasks) by assignee — scoped to the
  // Sales-profile reps, or the selected rep, over the chosen period.
  const ownerIds = useMemo(() => userOptions.map((u) => u.value), [userOptions]);
  const userNameMap = useMemo(
    () => new Map(userOptions.map((u) => [u.value, u.label])),
    [userOptions]
  );
  const { byAssignee, loading: activityLoading } = useActivityMetrics(
    ownerIds,
    aeId || null,
    period
  );
  const activityTotals = useMemo(
    () =>
      byAssignee.reduce(
        (acc, a) => ({
          calls: acc.calls + a.calls,
          emails: acc.emails + a.emails,
          tasks: acc.tasks + a.tasks,
          total: acc.total + a.total,
        }),
        { calls: 0, emails: 0, tasks: 0, total: 0 }
      ),
    [byAssignee]
  );

  // Opportunity stage picklist — powers the editable Stage dropdown.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    getPicklistValues('Opportunity', 'StageName', controller.signal)
      .then((opts) => {
        if (!cancelled) setStageOptions(opts);
      })
      .catch(() => {
        /* non-fatal: cell falls back to the row's existing stage */
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const openOpps = useMemo(
    () => opportunities.filter((o) => o.StageName !== 'Closed Won' && o.StageName !== 'Closed Lost'),
    [opportunities]
  );
  const closedWonOpps = useMemo(
    () => opportunities.filter((o) => o.StageName === 'Closed Won'),
    [opportunities]
  );
  const closedLostOpps = useMemo(
    () => opportunities.filter((o) => o.StageName === 'Closed Lost'),
    [opportunities]
  );

  const openPipeline = useMemo(
    () => openOpps.reduce((sum, o) => sum + (o.Amount || 0), 0),
    [openOpps]
  );
  const closedWonAmount = useMemo(
    () => closedWonOpps.reduce((sum, o) => sum + (o.Amount || 0), 0),
    [closedWonOpps]
  );

  const sourceOptions = useMemo(
    () =>
      [...new Set(opportunities.map((o) => o.LeadSource).filter(Boolean))].map(
        (s) => ({ value: s as string, label: s as string })
      ),
    [opportunities]
  );

  const refreshAll = useCallback(() => {
    refetch();
    refetchFunnel();
  }, [refetch, refetchFunnel]);

  // Rebuild columns when the stage picklist or refresh handler changes so the
  // editable Stage cell always has fresh options and can re-bucket rows on save.
  const aeColumns = useMemo(
    () => buildAeColumns(stageOptions, refreshAll),
    [stageOptions, refreshAll]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Sales</h1>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <SearchableSelect options={sourceOptions} value={source} onChange={(v) => setSource(v as string)} placeholder="All Sources" />
        <SearchableSelect options={userOptions} value={aeId} onChange={(v) => setAeId(v as string)} placeholder="All AEs" />
        <SearchableSelect options={TIME_PERIOD_OPTIONS} value={period} onChange={(v) => setPeriod((v as TimePeriod) || 'all_time')} placeholder="Time Period" />
        <button onClick={refreshAll} className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Refresh</button>
      </div>

      <FunnelSection stages={stages} />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard title="Demos Booked" value={demosScheduledCount} />
        <KPICard title="Open Pipeline" value={formatCurrency(openPipeline)} />
        <KPICard title="Closed Won" value={formatCurrency(closedWonAmount)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BreakdownCard title="Closed Won by Reason" rows={closedWonOpps} getKey={(o) => o.Won_Reason__c} />
        <BreakdownCard title="Closed Lost by Reason" rows={closedLostOpps} getKey={(o) => o.Loss_Reason__c} />
        <BreakdownCard title="Opportunity Source" rows={opportunities} getKey={(o) => o.LeadSource} />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Activity (Completed)</h2>
          <span className="text-xs text-gray-500">Calls, emails &amp; tasks completed by assignee</span>
        </div>
        {activityLoading || usersLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-6 w-6 border-4 border-gray-200 border-t-eep-navy rounded-full" />
          </div>
        ) : byAssignee.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No completed activity in this period</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Assignee</th>
                  <th className="py-2 px-4 font-medium text-right">Calls</th>
                  <th className="py-2 px-4 font-medium text-right">Emails</th>
                  <th className="py-2 px-4 font-medium text-right">Tasks</th>
                  <th className="py-2 pl-4 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {byAssignee.map((a) => (
                  <tr key={a.ownerId} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900">{userNameMap.get(a.ownerId) ?? a.ownerId}</td>
                    <td className="py-2 px-4 text-right text-gray-700">{formatNumber(a.calls)}</td>
                    <td className="py-2 px-4 text-right text-gray-700">{formatNumber(a.emails)}</td>
                    <td className="py-2 px-4 text-right text-gray-700">{formatNumber(a.tasks)}</td>
                    <td className="py-2 pl-4 text-right font-semibold text-gray-900">{formatNumber(a.total)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
                  <td className="py-2 pr-4">Total</td>
                  <td className="py-2 px-4 text-right">{formatNumber(activityTotals.calls)}</td>
                  <td className="py-2 px-4 text-right">{formatNumber(activityTotals.emails)}</td>
                  <td className="py-2 px-4 text-right">{formatNumber(activityTotals.tasks)}</td>
                  <td className="py-2 pl-4 text-right">{formatNumber(activityTotals.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Open Opportunities ({openOpps.length})</h2>
        <DataTable
          data={openOpps as unknown as Record<string, unknown>[]}
          columns={aeColumns as ColumnDef<Record<string, unknown>>[]}
          loading={loading || usersLoading}
          error={error}
          exportFilename="sales-open-opportunities"        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-green-700 mb-4">Closed Won ({closedWonOpps.length})</h2>
        <DataTable
          data={closedWonOpps as unknown as Record<string, unknown>[]}
          columns={aeColumns as ColumnDef<Record<string, unknown>>[]}
          loading={loading || usersLoading}
          error={error}
          exportFilename="sales-closed-won"        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-red-700 mb-4">Closed Lost ({closedLostOpps.length})</h2>
        <DataTable
          data={closedLostOpps as unknown as Record<string, unknown>[]}
          columns={aeColumns as ColumnDef<Record<string, unknown>>[]}
          loading={loading || usersLoading}
          error={error}
          exportFilename="sales-closed-lost"        />
      </div>
    </div>
  );
}
